import { Mailbox } from "@/data/models/Mailbox";
import { UserLookup } from "@/data/models/UserLookup";
import {
  HyDryEventMessagesFilter,
  HyDryMessageFilterType,
} from "./HyDryEventMessagesFilter";
import { IresDevice } from "@/data/models/IresDevice";
import { BranchFilter } from "@/data/models/BranchFilter";
import { HyDryMessagesSettings } from "@/data/models/HyDryMessagesConfig";
import {
  HyDryEventCategory,
  LatestHyDryEventMessageInfo,
} from "@/data/models/HyDryEvent";
import { compareHyDryEventMessages } from "@/data/repository/HyDryLogsRepository";

export interface MessagesInMailboxInfo {
  mailboxId: string;
  mailboxName: string;
  messages: LatestHyDryEventMessageInfo[];
}

export class MailboxFilter {
  userLookup: UserLookup;

  mailboxes: Mailbox[];
  filtersForMailbox: Map<string, HyDryEventMessagesFilter[]>;

  constructor(
    mailboxes: Mailbox[],
    userLookup: UserLookup,
    branchFilter: BranchFilter,
    iresDevices: IresDevice[],
    hyDryMessageSettings: HyDryMessagesSettings
  ) {
    this.userLookup = userLookup;

    this.mailboxes = mailboxes.sort(
      (a, b) => (a.sortValue ?? 1) - (b.sortValue ?? 1)
    );

    this.filtersForMailbox = new Map<string, HyDryEventMessagesFilter[]>();

    mailboxes.forEach((mailbox) => {
      const filtersForMailbox: HyDryEventMessagesFilter[] = [];

      mailbox.collectMessagesFromUserIds.forEach((userId) => {
        const user = userLookup.getUser(userId);

        if (user) {
          filtersForMailbox.push(
            HyDryEventMessagesFilter.createFilterFromSettings(
              user,
              branchFilter,
              iresDevices,
              hyDryMessageSettings
            )
          );
        }
      });

      this.filtersForMailbox.set(mailbox.id, filtersForMailbox);
    });
  }

  getMessagesInAllMailboxes(
    messages: LatestHyDryEventMessageInfo[]
  ): MessagesInMailboxInfo[] {
    return this.mailboxes.map((mailbox) =>
      this.filterMessagesForMailbox(mailbox, messages)
    );
  }

  filterMessagesForMailbox(
    mailbox: Mailbox,
    messages: LatestHyDryEventMessageInfo[]
  ): MessagesInMailboxInfo {
    const filtersForMailbox = this.filtersForMailbox.get(mailbox.id) ?? [];

    const filteredMessages = filtersForMailbox.flatMap((filter) =>
      filter.filterMyMessages(messages)
    );

    const messagesGroupedBySerialNumber = new Map<
      string,
      LatestHyDryEventMessageInfo
    >();

    filteredMessages.forEach((message) => {
      const combinedMessage = messagesGroupedBySerialNumber.get(
        message.serialNumber
      );

      if (!combinedMessage) {
        messagesGroupedBySerialNumber.set(message.serialNumber, message);
      } else {
        //Avoid counting messages for the same category multiple times
        const unreadMessagesNotAlreadyIncluded = message.unreadMessages.filter(
          (newMessage) =>
            !combinedMessage.unreadMessages.some(
              (alreadyIncludedMessage) =>
                alreadyIncludedMessage.eventCategory ===
                newMessage.eventCategory
            )
        );

        combinedMessage.unreadMessages.push(
          ...unreadMessagesNotAlreadyIncluded
        );
        combinedMessage.unreadMessages = combinedMessage.unreadMessages.sort(
          (a, b) => compareHyDryEventMessages(a.latestMessage, b.latestMessage)
        );
      }
    });

    const messagesInMailbox = [...messagesGroupedBySerialNumber.values()].sort(
      (a, b) =>
        b.unreadMessages[0].latestMessage.messageDate.getTime() -
        a.unreadMessages[0].latestMessage.messageDate.getTime()
    );

    return {
      mailboxName: mailbox.name,
      mailboxId: mailbox.id,
      messages: messagesInMailbox,
    };
  }

  updateVisibleHyDryEventCategories(visibleCategories: HyDryEventCategory[]) {
    this.filtersForMailbox.forEach((value) =>
      value.forEach((filter) => {
        filter.updateVisibleHyDryEventCategories(
          HyDryMessageFilterType.MY_MESSAGES,
          visibleCategories
        );
      })
    );
  }
}
